import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaShoppingCart,
  FaTrash,
  FaPlus,
  FaMinus,
  FaArrowLeft,
  FaCreditCard,
  FaTimes,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
  FaUpload,
  FaQrcode,
  FaCheck,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaChevronLeft,
  FaArrowRight
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Firebase imports
import { database, storage } from '../firebase';
import { ref as dbRef, get, set, onValue } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Payment checkout states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState(1); // 1: Delivery, 2: Payment, 3: Confirmation
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentScreenshotPreview, setPaymentScreenshotPreview] = useState('');
  const [upiId, setUpiId] = useState('');
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [paymentQRCodes, setPaymentQRCodes] = useState({ googlePay: '', phonePe: '' });
  const [qrLoading, setQrLoading] = useState(true);


  useEffect(() => {
    // Load cart data
    const userId = localStorage.getItem('userId');
    const userMobile = localStorage.getItem('userMobile');

    // Check if user is logged in
    const isLoggedIn = userId && userMobile;

    const loadCart = async () => {
      setLoading(true);

      if (isLoggedIn) {
        // Get cart data from Firebase for logged-in user
        try {
          const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
          const snapshot = await get(cartRef);

          if (snapshot.exists()) {
            const cartData = snapshot.val();
            // Convert Firebase object to array
            const cartArray = Object.keys(cartData).map(key => ({
              ...cartData[key],
              firebaseId: key
            }));
            setCartItems(cartArray);
          } else {
            setCartItems([]);
          }
        } catch (error) {
          console.error('Error loading cart from Firebase:', error);
          // Fallback to localStorage if Firebase fails
          const cartData = JSON.parse(localStorage.getItem(`cart_${userId}`) || '[]');
          setCartItems(cartData);
        }
      } else {
        // Load from localStorage for guest user
        const cartData = JSON.parse(localStorage.getItem('guest_cart') || '[]');
        setCartItems(cartData);
      }

      setLoading(false);
    };

    loadCart();
  }, []);

  // Fetch payment QR codes from Firebase
  useEffect(() => {
    const qrRef = dbRef(database, 'Dr/paymentQR');

    const unsubscribe = onValue(qrRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setPaymentQRCodes({
            googlePay: data.googlePay || '',
            phonePe: data.phonePe || ''
          });
        }
        setQrLoading(false);
      } catch (err) {
        console.error('Error fetching payment QR codes:', err);
        setQrLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Pre-fill phone number when modal opens
  useEffect(() => {
    if (showPaymentModal) {
      const userMobile = localStorage.getItem('userMobile');
      if (userMobile) {
        setPhoneNumber(userMobile);
      }
    }
  }, [showPaymentModal]);



  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;

    const updatedCart = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );

    setCartItems(updatedCart);

    // Update Storage (Firebase or LocalStorage)
    const userMobile = localStorage.getItem('userMobile');

    if (userMobile) {
      // Update Firebase for logged-in user
      const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
      const updatedCartData = {};

      updatedCart.forEach(item => {
        const itemKey = item.firebaseId || `item_${item.id}_${Date.now()}`;
        updatedCartData[itemKey] = {
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image,
          category: item.category,
          description: item.description,
          updatedAt: new Date().toISOString()
        };
      });

      set(cartRef, updatedCartData).catch(error => {
        console.error('Error updating cart in Firebase:', error);
      });
    } else {
      // Update LocalStorage for guest user
      localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    }
  };

  const removeItem = (id) => {
    const itemToRemove = cartItems.find(item => item.id === id);
    const updatedCart = cartItems.filter(item => item.id !== id);
    setCartItems(updatedCart);

    // Update Storage (Firebase or LocalStorage)
    const userMobile = localStorage.getItem('userMobile');

    if (userMobile) {
      if (itemToRemove?.firebaseId) {
        const itemRef = dbRef(database, `Dr/users/${userMobile}/cart/${itemToRemove.firebaseId}`);
        // Remove the specific item
        set(itemRef, null).catch(error => {
          console.error('Error removing item from Firebase:', error);
        });
      }
    } else {
      // Update LocalStorage for guest user
      localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal();
  };

  // Payment handler functions
  const handlePaymentScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPaymentScreenshot(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPaymentScreenshotPreview(reader.result);
        };
        reader.readAsDataURL(file);
        setFormErrors(prev => ({ ...prev, screenshot: '' }));
      } else {
        setFormErrors(prev => ({ ...prev, screenshot: 'Please upload a valid image file' }));
      }
    }
  };

  const validateDeliveryDetails = () => {
    const errors = {};

    if (!deliveryAddress.trim() || deliveryAddress.trim().length < 10) {
      errors.address = 'Please enter a complete delivery address (minimum 10 characters)';
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneNumber.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(phoneNumber.trim())) {
      errors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    return errors;
  };

  const validatePaymentDetails = () => {
    const errors = {};

    if (!selectedPaymentMethod) {
      errors.paymentMethod = 'Please select a payment method';
    }

    if (!paymentScreenshot) {
      errors.screenshot = 'Please upload payment screenshot';
    }

    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    if (!upiId.trim()) {
      errors.upiId = 'UPI ID is required';
    } else if (!upiRegex.test(upiId.trim())) {
      errors.upiId = 'Please enter a valid UPI ID (e.g., username@paytm)';
    }

    return errors;
  };

  const handleContinueToPayment = () => {
    const errors = validateDeliveryDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setPaymentStep(2);
  };

  const handleSubmitOrder = async () => {
    const errors = validatePaymentDetails();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsProcessingOrder(true);
    setFormErrors({});

    try {
      // Get user details from localStorage
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const userMobile = localStorage.getItem('userMobile');

      console.log('Starting order submission for mobile:', userMobile);

      // Step 1: Upload payment screenshot to Firebase Storage
      let screenshotURL = '';
      try {
        const timestamp = Date.now();
        const screenshotFileName = `payment_${userMobile}_${timestamp}_${paymentScreenshot.name}`;
        const screenshotStorageRef = storageRef(storage, `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        console.log('Uploading screenshot to:', `Dr/orders/${userMobile}/screenshots/${screenshotFileName}`);

        // Upload the file
        const uploadResult = await uploadBytes(screenshotStorageRef, paymentScreenshot);
        console.log('Screenshot uploaded successfully');

        // Get the download URL
        screenshotURL = await getDownloadURL(uploadResult.ref);
        console.log('Screenshot URL:', screenshotURL);
      } catch (uploadError) {
        console.error('Error uploading screenshot:', uploadError);
        setFormErrors({ submit: 'Failed to upload payment screenshot. Please try again.' });
        setIsProcessingOrder(false);
        return;
      }

      // Step 2: Get the next order number for this mobile number
      const userOrdersRef = dbRef(database, `Dr/orders/${userMobile}`);
      const userOrdersSnapshot = await get(userOrdersRef);

      let nextOrderNumber = 1;
      if (userOrdersSnapshot.exists()) {
        const existingOrders = userOrdersSnapshot.val();
        // Get all numeric keys and find the maximum
        const orderNumbers = Object.keys(existingOrders)
          .map(key => parseInt(key))
          .filter(num => !isNaN(num));

        if (orderNumbers.length > 0) {
          nextOrderNumber = Math.max(...orderNumbers) + 1;
        }
      }

      console.log('Next order number for mobile', userMobile, ':', nextOrderNumber);

      // Step 3: Create order object
      const orderData = {
        orderId: `ORD${Date.now()}`,
        orderNumber: nextOrderNumber,
        userId: userId,
        userName: userName,
        userMobile: userMobile,
        product: {
          id: cartItems[0].id,
          name: cartItems[0].name,
          price: cartItems[0].price,
          quantity: cartItems[0].quantity,
          totalPrice: cartItems[0].price * cartItems[0].quantity,
          image: cartItems[0].image,
          category: cartItems[0].category,
          description: cartItems[0].description
        },
        deliveryDetails: {
          address: deliveryAddress,
          phoneNumber: phoneNumber
        },
        paymentDetails: {
          method: selectedPaymentMethod,
          upiId: upiId,
          screenshotURL: screenshotURL,
          screenshotName: paymentScreenshot.name,
          screenshotSize: paymentScreenshot.size
        },
        orderStatus: 'Pending Verification',
        paymentStatus: 'Pending Verification',
        timestamp: new Date().toISOString(),
        createdAt: Date.now()
      };

      // Step 4: Save order to Firebase under mobile number with sequential number
      const orderRef = dbRef(database, `Dr/orders/${userMobile}/${nextOrderNumber}`);
      await set(orderRef, orderData);

      console.log('Order saved successfully at:', `Dr/orders/${userMobile}/${nextOrderNumber}`);

      // Step 5: Clear the cart after successful order
      const cartRef = dbRef(database, `Dr/users/${userMobile}/cart`);
      await set(cartRef, null);
      setCartItems([]);

      setOrderId(orderData.orderId);
      setOrderSuccess(true);
      setPaymentStep(3);
      setIsProcessingOrder(false);

      // Reset form after 5 seconds
      setTimeout(() => {
        handleClosePaymentModal();
      }, 5000);

    } catch (error) {
      console.error('Error submitting order:', error);
      setFormErrors({ submit: 'Failed to submit order. Please try again.' });
      setIsProcessingOrder(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentStep(1);
    setDeliveryAddress('');
    setPhoneNumber('');
    setSelectedPaymentMethod('');
    setPaymentScreenshot(null);
    setPaymentScreenshotPreview('');
    setUpiId('');
    setFormErrors({});
    setOrderSuccess(false);
    setOrderId('');
  };

  const handleBackToDelivery = () => {
    setPaymentStep(1);
    setFormErrors({});
  };

  const handleBuyNow = () => {
    // Check if user is logged in
    const userId = localStorage.getItem('userId');
    const userName = localStorage.getItem('userName');

    if (!userId || !userName) {
      navigate('/login');
      return;
    }

    // Check if cart has items
    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    // Open payment modal
    setShowPaymentModal(true);
    setPaymentStep(1);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">

        {/* Header */}
        <div className="mb-8">
          {/* <Link 
            to="/products"
            className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-4 transition-colors"
           >
            <FaArrowLeft className="mr-2" />
            Continue Shopping
          </Link> */}

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 flex items-center">
                <FaShoppingCart className="mr-3 text-teal-600" />
                Shopping Cart
              </h1>
              <p className="text-gray-600 mt-2">
                {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart
              </p>
            </div>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
              <FaShoppingCart className="text-6xl text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Your cart is empty</h2>
              <p className="text-gray-600 mb-6">Looks like you haven't added anything to your cart yet.</p>
              <Link
                to="/products"
                className="inline-flex items-center bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
              >
                <FaShoppingCart className="mr-2" />
                Start Shopping
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800">Cart Items</h2>
                </div>

                <div className="divide-y divide-gray-200">
                  {cartItems.map((item) => (
                    <div key={item.id} className="p-6 hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col sm:flex-row gap-4">

                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                        </div>

                        {/* Product Details */}
                        <div className="flex-grow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-grow">
                              <h3 className="font-semibold text-gray-800 text-lg">{item.name}</h3>
                              <p className="text-sm text-gray-500 mb-1">{item.category}</p>
                              <p className="text-xs text-gray-400">{item.description}</p>
                            </div>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="text-red-500 hover:text-red-700 transition-colors p-2 ml-2"
                              title="Remove item"
                            >
                              <FaTrash />
                            </button>
                          </div>

                          <div className="flex justify-between items-center mt-3">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                                title="Decrease quantity"
                              >
                                <FaMinus className="text-xs" />
                              </button>
                              <span className="font-semibold w-8 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors"
                                title="Increase quantity"
                              >
                                <FaPlus className="text-xs" />
                              </button>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-teal-600">₹{item.price}</p>
                              <p className="text-sm text-gray-500">₹{item.price * item.quantity} total</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-4">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-800">Order Summary</h2>
                </div>

                <div className="p-6 space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                    <span className="font-semibold">₹{calculateSubtotal()}</span>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">Total</span>
                      <span className="text-2xl font-bold text-teal-600">₹{calculateTotal()}</span>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={handleBuyNow}
                      className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all text-center flex items-center justify-center space-x-2"
                    >
                      <FaCreditCard />
                      <span>Buy Now</span>
                    </button>
                  </div>

                  <div className="pt-4">
                    <Link
                      to="/products"
                      className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-center block"
                    >
                      Continue Shopping
                    </Link>
                  </div>

                  {/* <div className="mt-4 p-4 bg-teal-50 rounded-lg">
                    <p className="text-sm text-teal-700">
                      <strong>Free Delivery</strong> on orders above ₹500
                    </p>
                    <p className="text-xs text-teal-600 mt-1">
                      Estimated delivery: 3-5 business days
                    </p>
                  </div> */}

                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Secure Payment</strong> - 100% secure transactions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleClosePaymentModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-emerald-500 text-white p-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Checkout</h2>
                    <p className="text-teal-100 text-sm">
                      {paymentStep === 1 && "Step 1: Delivery Details"}
                      {paymentStep === 2 && "Step 2: Payment"}
                      {paymentStep === 3 && "Order Confirmed!"}
                    </p>
                  </div>
                  <button
                    onClick={handleClosePaymentModal}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="flex items-center mt-4 space-x-2">
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 1 ? 'bg-white' : 'bg-white/30'}`}></div>
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 2 ? 'bg-white' : 'bg-white/30'}`}></div>
                  <div className={`flex-1 h-2 rounded-full ${paymentStep >= 3 ? 'bg-white' : 'bg-white/30'}`}></div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Step 1: Delivery Details */}
                {paymentStep === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {/* Cart Summary */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 mb-6">
                      <h3 className="font-bold text-gray-800 mb-3">Order Summary</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Items:</span>
                          <span className="font-bold">{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-bold text-teal-600">₹{calculateTotal()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Address */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaMapMarkerAlt className="inline mr-2 text-teal-500" />
                        Delivery Address *
                      </label>
                      <textarea
                        value={deliveryAddress}
                        onChange={(e) => {
                          setDeliveryAddress(e.target.value);
                          setFormErrors(prev => ({ ...prev, address: '' }));
                        }}
                        placeholder="Enter your complete delivery address including house/flat number, street, area, city, state, and pincode"
                        rows="4"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.address ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.address && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.address}
                        </p>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaPhone className="inline mr-2 text-teal-500" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => {
                          setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                          setFormErrors(prev => ({ ...prev, phone: '' }));
                        }}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.phone ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.phone && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.phone}
                        </p>
                      )}
                    </div>

                    {/* Continue Button */}
                    <button
                      onClick={handleContinueToPayment}
                      className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center space-x-2"
                    >
                      <span>Continue to Payment</span>
                      <FaArrowRight />
                    </button>
                  </motion.div>
                )}

                {/* Step 2: Payment */}
                {paymentStep === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {/* Order Summary */}
                    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-4 mb-6 border border-teal-200">
                      <h3 className="font-bold text-gray-800 mb-3">Order Summary</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Product Total:</span>
                          <span className="font-bold">₹{calculateTotal()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Delivery:</span>
                          <span className="font-bold text-green-600">FREE</span>
                        </div>
                        <div className="border-t border-teal-200 pt-2 mt-2 flex justify-between">
                          <span className="font-bold text-gray-800">Total Amount:</span>
                          <span className="font-bold text-xl text-teal-600">₹{calculateTotal()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        <FaCreditCard className="inline mr-2 text-teal-500" />
                        Select Payment Method *
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {/* Google Pay */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedPaymentMethod('GooglePay');
                            setFormErrors(prev => ({ ...prev, paymentMethod: '' }));
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'GooglePay'
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-4xl mb-2">💳</div>
                            <p className="font-bold text-gray-800">Google Pay</p>
                          </div>
                        </motion.button>

                        {/* PhonePe */}
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedPaymentMethod('PhonePe');
                            setFormErrors(prev => ({ ...prev, paymentMethod: '' }));
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${selectedPaymentMethod === 'PhonePe'
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="text-center">
                            <div className="text-4xl mb-2">📱</div>
                            <p className="font-bold text-gray-800">PhonePe</p>
                          </div>
                        </motion.button>
                      </div>
                      {formErrors.paymentMethod && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.paymentMethod}
                        </p>
                      )}
                    </div>

                    {/* QR Code Display */}
                    {selectedPaymentMethod && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 text-center"
                      >
                        <FaQrcode className="text-6xl text-gray-400 mx-auto mb-4" />
                        <h4 className="font-bold text-gray-800 mb-2">
                          Scan QR Code to Pay
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Pay ₹{calculateTotal()} using {selectedPaymentMethod}
                        </p>
                        <div className="bg-white p-4 rounded-xl inline-block">
                          <p className="text-xs text-gray-500 mb-2">
                            {selectedPaymentMethod === 'GooglePay' ? 'Google Pay QR Code' : 'PhonePe QR Code'}
                          </p>
                          {qrLoading ? (
                            <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                              <FaSpinner className="text-3xl text-gray-400 animate-spin" />
                            </div>
                          ) : (
                            <div className="w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                              {(selectedPaymentMethod === 'GooglePay' && paymentQRCodes.googlePay) ||
                                (selectedPaymentMethod === 'PhonePe' && paymentQRCodes.phonePe) ? (
                                <img
                                  src={selectedPaymentMethod === 'GooglePay' ? paymentQRCodes.googlePay : paymentQRCodes.phonePe}
                                  alt={`${selectedPaymentMethod} QR Code`}
                                  className="w-full h-full object-contain"
                                />
                              ) : (
                                <div className="text-center p-4">
                                  <FaQrcode className="text-4xl text-gray-400 mx-auto mb-2" />
                                  <p className="text-gray-500 text-xs">QR Code not available</p>
                                  <p className="text-gray-400 text-xs mt-1">Please contact admin</p>
                                </div>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            Scan to complete payment
                          </p>
                        </div>
                      </motion.div>
                    )}

                    {/* Payment Screenshot Upload */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaUpload className="inline mr-2 text-teal-500" />
                        Upload Payment Screenshot *
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePaymentScreenshotUpload}
                          className="hidden"
                          id="screenshot-upload"
                        />
                        <label
                          htmlFor="screenshot-upload"
                          className={`block w-full p-4 border-2 border-dashed rounded-xl cursor-pointer hover:border-teal-500 transition-all ${formErrors.screenshot ? 'border-red-300' : 'border-gray-300'
                            }`}
                        >
                          {paymentScreenshotPreview ? (
                            <div className="text-center">
                              <img
                                src={paymentScreenshotPreview}
                                alt="Payment Screenshot"
                                className="max-h-40 mx-auto rounded-lg mb-2"
                              />
                              <p className="text-sm text-green-600 font-medium">
                                <FaCheckCircle className="inline mr-2" />
                                Screenshot uploaded successfully
                              </p>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500">
                              <FaUpload className="text-3xl mx-auto mb-2" />
                              <p className="text-sm">Click to upload payment screenshot</p>
                            </div>
                          )}
                        </label>
                      </div>
                      {formErrors.screenshot && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.screenshot}
                        </p>
                      )}
                    </div>

                    {/* UPI ID */}
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        <FaUser className="inline mr-2 text-teal-500" />
                        Your UPI ID *
                      </label>
                      <input
                        type="text"
                        value={upiId}
                        onChange={(e) => {
                          setUpiId(e.target.value);
                          setFormErrors(prev => ({ ...prev, upiId: '' }));
                        }}
                        placeholder="yourname@paytm or yourname@phonepe"
                        className={`w-full p-4 border-2 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all ${formErrors.upiId ? 'border-red-300' : 'border-gray-200'
                          }`}
                      />
                      {formErrors.upiId && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <FaExclamationTriangle className="mr-2" />
                          {formErrors.upiId}
                        </p>
                      )}
                    </div>

                    {/* Error Message */}
                    {formErrors.submit && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                        <FaExclamationTriangle className="inline mr-2" />
                        {formErrors.submit}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                      <button
                        onClick={handleBackToDelivery}
                        className="flex-1 bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition-all flex items-center justify-center space-x-2"
                      >
                        <FaChevronLeft />
                        <span>Back</span>
                      </button>
                      <button
                        onClick={handleSubmitOrder}
                        disabled={isProcessingOrder}
                        className={`flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-4 rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center space-x-2 ${isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                      >
                        {isProcessingOrder ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>
                            <FaCheck />
                            <span>Confirm Order</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Confirmation */}
                {paymentStep === 3 && orderSuccess && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
                    >
                      <FaCheckCircle className="text-5xl text-white" />
                    </motion.div>

                    <h3 className="text-3xl font-bold text-gray-800 mb-2">
                      Order Placed Successfully!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Your order has been received and is being processed
                    </p>

                    <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-6 mb-6 border border-teal-200">
                      <p className="text-sm text-gray-600 mb-2">Order ID</p>
                      <p className="text-2xl font-bold text-teal-600 mb-4">{orderId}</p>

                      <div className="text-left space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Items:</span>
                          <span className="font-medium">{cartItems.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-bold text-teal-600">₹{calculateTotal()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Method:</span>
                          <span className="font-medium">{selectedPaymentMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className="font-medium text-orange-600">Pending Verification</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <FaExclamationTriangle className="inline mr-2" />
                        Your payment will be verified shortly. You will receive a confirmation once verified.
                      </p>
                    </div>

                    <button
                      onClick={handleClosePaymentModal}
                      className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-8 py-4 rounded-xl font-bold hover:shadow-xl transition-all"
                    >
                      Continue Shopping
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Cart;
